import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  Box,
  Flex,
  Text,
  Button,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  useToast,
  useColorModeValue,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  SimpleGrid,
  HStack,
  VStack,
  Progress,
  Alert,
  AlertIcon,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  Wrap,
  WrapItem,
  IconButton,
  Tooltip,
  Tag,
  TagLabel,
  Stepper,
  Step,
  StepNumber,
  StepIcon,
  StepStatus,
  StepIndicator,
  StepSeparator,
  Heading,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  Portal,
} from "@chakra-ui/react";
import { ArrowBackIcon, DeleteIcon, WarningIcon } from "@chakra-ui/icons";
import {
  FiBarChart2,
  FiRefreshCw,
  FiTrendingUp,
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
} from "react-icons/fi";
import { BsActivity, BsLink45Deg, BsShieldLock } from "react-icons/bs";
import { projectService } from "../services/edosService";
import api from "../services/api";

const STATUS_COLORS = {
  draft: "gray",
  planning: "blue",
  active: "green",
  on_hold: "orange",
  uat: "cyan",
  deployment: "purple",
  completed: "teal",
  cancelled: "red",
  archived: "blackAlpha",
};

const PRIORITY_COLORS = {
  Low: "blue",
  Medium: "yellow",
  High: "orange",
  Critical: "red",
};

const WORKFLOW_STATE_COLORS = {
  open: "blue",
  in_progress: "orange",
  resolved: "green",
  closed: "gray",
  reopened: "purple",
  pending: "yellow",
};

// Mirrors backend STATUS_TRANSITIONS map exactly
const STATUS_TRANSITIONS = {
  draft: ["planning", "cancelled"],
  planning: ["active", "on_hold", "cancelled"],
  active: ["on_hold", "uat", "completed", "cancelled"],
  on_hold: ["active", "cancelled"],
  uat: ["deployment", "active", "cancelled"],
  deployment: ["completed", "active", "cancelled"],
  completed: ["archived"],
  cancelled: ["archived"],
  archived: [],
};

// Semantic status configuration for enterprise UX
const SEMANTIC_STATUS = {
  draft: {
    icon: FiClock,
    colorScheme: "gray",
    label: "Draft",
    description: "Ready to submit",
  },
  planning: {
    icon: FiBarChart2,
    colorScheme: "blue",
    label: "Planning",
    description: "In planning phase",
  },
  active: {
    icon: FiCheckCircle,
    colorScheme: "green",
    label: "Active",
    description: "Currently executing",
  },
  on_hold: {
    icon: FiAlertCircle,
    colorScheme: "orange",
    label: "On Hold",
    description: "Temporarily paused",
  },
  uat: {
    icon: FiBarChart2,
    colorScheme: "cyan",
    label: "UAT",
    description: "Testing phase",
  },
  deployment: {
    icon: FiTrendingUp,
    colorScheme: "purple",
    label: "Deployment",
    description: "Deployment in progress",
  },
  completed: {
    icon: FiCheckCircle,
    colorScheme: "teal",
    label: "Completed",
    description: "Successfully finished",
  },
  cancelled: {
    icon: FiAlertCircle,
    colorScheme: "red",
    label: "Cancelled",
    description: "Cancelled",
  },
  archived: {
    icon: FiClock,
    colorScheme: "blackAlpha",
    label: "Archived",
    description: "Historical record",
  },
};

function InfoCard({ label, value, color }) {
  const bg = useColorModeValue("gray.50", "gray.700");
  return (
    <Box p={4} bg={bg} borderRadius="md">
      <Text fontSize="xs" color="gray.500" mb={1}>
        {label}
      </Text>
      <Text fontWeight="semibold" textTransform="capitalize" color={color}>
        {String(value ?? "—").replace(/_/g, " ")}
      </Text>
    </Box>
  );
}

// Helper: Calculate project health score (0-100)
function calculateHealthScore(project, tickets) {
  if (!project) return 0;

  let score = 100;
  const overdueTickets =
    tickets?.filter((t) => {
      if (!t.dueDate) return false;
      return (
        new Date(t.dueDate) < new Date() &&
        !["resolved", "closed", "completed"].includes(t.workflowState)
      );
    }).length || 0;

  const openTickets =
    tickets?.filter(
      (t) => !["resolved", "closed", "completed"].includes(t.workflowState),
    ).length || 0;

  // Deduct points for risk factors
  if (overdueTickets > 0) score -= Math.min(30, overdueTickets * 5);
  if (project.status === "on_hold") score -= 15;
  if (project.priority === "Critical") score -= 10;

  return Math.max(0, Math.min(100, score));
}

// Helper: Get health status badge
function getHealthStatus(score) {
  if (score >= 80)
    return { status: "Healthy", color: "green", icon: FiCheckCircle };
  if (score >= 50)
    return { status: "At Risk", color: "orange", icon: FiAlertCircle };
  return { status: "Critical", color: "red", icon: WarningIcon };
}

// Enhanced empty state component
function EmptyStateCard({ icon: Icon, title, description, action }) {
  return (
    <VStack py={20} align="center" justify="center" spacing={4}>
      {Icon && <Icon size={48} color="gray" opacity={0.6} />}
      <Heading size="md">{title}</Heading>
      <Text color="gray.600" maxW="sm" textAlign="center">
        {description}
      </Text>
      {action && action}
    </VStack>
  );
}

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("gray.50", "gray.700");

  const [project, setProject] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashLoading, setDashLoading] = useState(false);
  const [transitioning, setTransitioning] = useState(null);
  const [projectTickets, setProjectTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);

  // Filter state
  const [ticketFilters, setTicketFilters] = useState({
    status: [],
    priority: [],
    severity: [],
    assignedTo: [],
    search: "",
  });
  const [filterOptions, setFilterOptions] = useState({
    statuses: [],
    priorities: [],
    severities: [],
  });

  const normalizeMultiValues = (values) => {
    if (Array.isArray(values)) return values;
    if (!values) return [];
    return [values];
  };

  const getMultiSelectLabel = (selectedValues, options, placeholder) => {
    if (!selectedValues?.length) return placeholder;
    if (selectedValues.length === 1) {
      return (
        options.find((option) => option.value === selectedValues[0])?.label ||
        selectedValues[0]
      );
    }
    return `${selectedValues.length} selected`;
  };

  const assigneeOptions = useMemo(() => {
    const seen = new Map();
    projectTickets.forEach((ticket) => {
      const id = ticket?.assignedTo?._id;
      if (!id) return;
      const label =
        ticket.assignedTo?.name || ticket.assignedTo?.email || "Unknown";
      if (!seen.has(id)) {
        seen.set(id, { value: id, label });
      }
    });
    return Array.from(seen.values());
  }, [projectTickets]);

  const hasActiveFilters = useMemo(
    () =>
      ticketFilters.status.length > 0 ||
      ticketFilters.priority.length > 0 ||
      ticketFilters.severity.length > 0 ||
      ticketFilters.assignedTo.length > 0 ||
      Boolean(ticketFilters.search.trim()),
    [ticketFilters],
  );

  const normalizeTicketRows = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.tickets)) return payload.data.tickets;
    if (Array.isArray(payload?.tickets)) return payload.tickets;
    return [];
  };

  const fetchProject = useCallback(async () => {
    setLoading(true);
    try {
      const res = await projectService.getById(id);
      setProject(res.data.data || res.data.project || res.data);
    } catch (err) {
      toast({ title: "Failed to load project", status: "error" });
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  const filteredTickets = useMemo(() => {
    let filtered = projectTickets;

    if (ticketFilters.status.length > 0) {
      filtered = filtered.filter((t) =>
        ticketFilters.status.includes(t.workflowState),
      );
    }

    if (ticketFilters.priority.length > 0) {
      filtered = filtered.filter((t) =>
        ticketFilters.priority.includes(t.priority),
      );
    }

    if (ticketFilters.severity.length > 0) {
      filtered = filtered.filter((t) =>
        ticketFilters.severity.includes(t.severity),
      );
    }

    if (ticketFilters.assignedTo.length > 0) {
      filtered = filtered.filter((t) =>
        ticketFilters.assignedTo.includes(t.assignedTo?._id),
      );
    }

    if (ticketFilters.search.trim()) {
      const searchTerm = ticketFilters.search.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.title?.toLowerCase().includes(searchTerm) ||
          t.ticketId?.toLowerCase().includes(searchTerm) ||
          t.description?.toLowerCase().includes(searchTerm),
      );
    }

    return filtered;
  }, [projectTickets, ticketFilters]);

  const displayedTickets = hasActiveFilters ? filteredTickets : projectTickets;

  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const res = await api.get("/api/tickets/filter-options");
        if (res.data.success && res.data.data) {
          setFilterOptions(res.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch filter options:", err);
      }
    };
    fetchFilterOptions();
  }, []);

  const fetchDashboard = useCallback(async () => {
    setDashLoading(true);
    try {
      const res = await projectService.dashboard(id);
      setDashboard(res.data.data || res.data);
    } catch (_) {
    } finally {
      setDashLoading(false);
    }
  }, [id]);

  // Excel export for filtered tickets
  const handleExportTickets = useCallback(() => {
    const toExport = hasActiveFilters ? filteredTickets : projectTickets;
    if (toExport.length === 0) {
      toast({
        title: "No tickets to export",
        description: "Apply filters or create tickets first",
        status: "info",
      });
      return;
    }

    const excelData = toExport.map((ticket) => ({
      "Ticket ID": ticket.ticketId || "-",
      Title: ticket.title || "-",
      Status: (ticket.workflowState || "open").replace(/_/g, " "),
      Priority: ticket.priority || "-",
      Severity: ticket.severity || "-",
      "Due Date": ticket.dueDate
        ? new Date(ticket.dueDate).toLocaleDateString()
        : "-",
      "Assigned To": ticket.assignedTo?.name || "Unassigned",
      "Created Date": ticket.createdAt
        ? new Date(ticket.createdAt).toLocaleDateString()
        : "-",
      "Updated Date": ticket.updatedAt
        ? new Date(ticket.updatedAt).toLocaleDateString()
        : "-",
      "Story Points": ticket.storyPoints || "-",
      Sprint: ticket.sprintId?.sprintName || "-",
      Project: project?.projectName || "-",
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tickets");

    const colWidths = [12, 25, 30, 15, 12, 12, 15, 20, 15, 15, 12, 15, 20];
    ws["!cols"] = colWidths.map((w) => ({ wch: w }));

    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `${project?.projectName || "project"}-tickets-${timestamp}.xlsx`;

    XLSX.writeFile(wb, filename);
    toast({ title: "Tickets exported successfully", status: "success" });
  }, [filteredTickets, projectTickets, project, toast, hasActiveFilters]);
  const fetchProjectTickets = useCallback(async () => {
    setTicketsLoading(true);
    try {
      const allRows = [];
      let page = 1;
      let totalPages = 1;

      do {
        const res = await api.get("/api/tickets", {
          params: {
            page,
            limit: 100,
            projectId: id,
            _viewMode: "all",
            sortBy: "createdAt",
            sortOrder: "desc",
          },
        });

        const payload = res?.data;
        const rows = normalizeTicketRows(payload);
        allRows.push(...rows);

        totalPages = Number(
          payload?.totalPages || payload?.data?.totalPages || 1,
        );
        page += 1;
      } while (page <= totalPages && page <= 50);

      setProjectTickets(allRows);
    } catch {
      setProjectTickets([]);
      toast({ title: "Failed to load project tickets", status: "error" });
    } finally {
      setTicketsLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    fetchProject();
    fetchDashboard();
    fetchProjectTickets();
  }, [fetchProject, fetchDashboard, fetchProjectTickets]);

  const handleTransition = async (status) => {
    setTransitioning(status);
    try {
      await projectService.transition(id, { status });
      toast({
        title: `Status updated to "${status.replace(/_/g, " ")}"`,
        status: "success",
      });
      fetchProject();
      fetchDashboard();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Transition failed",
        status: "error",
      });
    } finally {
      setTransitioning(null);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        `Delete project "${project?.projectName}"? This cannot be undone.`,
      )
    )
      return;
    try {
      await projectService.remove(id);
      toast({ title: "Project deleted", status: "success" });
      navigate("/edos/projects");
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed to delete",
        status: "error",
      });
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="60vh">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  if (!project) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          Project not found.
        </Alert>
        <Button
          mt={4}
          leftIcon={<ArrowBackIcon />}
          onClick={() => navigate("/edos/projects")}
        >
          Back to Projects
        </Button>
      </Box>
    );
  }

  // Calculate health score
  const healthScore = calculateHealthScore(project, projectTickets);
  const health = getHealthStatus(healthScore);
  const HealthIcon = health.icon;

  return (
    <Box p={6}>
      {/* Page Header */}
      <Flex
        justify="space-between"
        align="flex-start"
        mb={6}
        wrap="wrap"
        gap={3}
      >
        <HStack align="flex-start" spacing={3}>
          <Button
            leftIcon={<ArrowBackIcon />}
            variant="ghost"
            onClick={() => navigate("/edos/projects")}
            size="sm"
          >
            Projects
          </Button>
          <Box>
            <HStack spacing={2} align="center">
              <Text fontSize="2xl" fontWeight="bold">
                {project.projectName}
              </Text>
              <Badge
                colorScheme={STATUS_COLORS[project.status] || "gray"}
                fontSize="sm"
                px={2}
                py={1}
              >
                {project.status?.replace(/_/g, " ")}
              </Badge>
              {project.priority && (
                <Badge
                  colorScheme={PRIORITY_COLORS[project.priority] || "gray"}
                  fontSize="sm"
                >
                  {project.priority}
                </Badge>
              )}
              <Badge colorScheme="blue" fontSize="sm">
                {projectTickets.length} tickets
              </Badge>
            </HStack>
            <Text fontSize="sm" color="gray.500" mt={1}>
              {project.projectCode} &nbsp;·&nbsp; {project.projectType}
            </Text>
          </Box>
        </HStack>

        <HStack spacing={2}>
          <Tooltip label="View Sprints">
            <IconButton
              icon={<FiBarChart2 />}
              variant="outline"
              colorScheme="purple"
              size="sm"
              aria-label="Sprints"
              onClick={() => navigate(`/edos/sprints?projectId=${project._id}`)}
            />
          </Tooltip>
          <Tooltip label="Refresh">
            <IconButton
              icon={<FiRefreshCw />}
              variant="outline"
              size="sm"
              aria-label="Refresh"
              onClick={() => {
                fetchProject();
                fetchDashboard();
                fetchProjectTickets();
              }}
            />
          </Tooltip>
          <Tooltip label="Delete Project">
            <IconButton
              icon={<DeleteIcon />}
              variant="outline"
              colorScheme="red"
              size="sm"
              aria-label="Delete"
              onClick={handleDelete}
            />
          </Tooltip>
        </HStack>
      </Flex>

      {/* KPI Metrics Strip */}
      <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing={3} mb={6}>
        <Box
          p={3}
          bg={useColorModeValue(`${health.color}.50`, `${health.color}.900`)}
          borderRadius="md"
          border="1px"
          borderColor={useColorModeValue(
            `${health.color}.200`,
            `${health.color}.700`,
          )}
        >
          <HStack spacing={2}>
            <HealthIcon color={`${health.color}.500`} size={20} />
            <Box>
              <Text fontSize="xs" color="gray.600" fontWeight="bold">
                Health
              </Text>
              <Text fontSize="sm" fontWeight="bold">
                {health.status} ({healthScore}%)
              </Text>
            </Box>
          </HStack>
        </Box>

        <Box
          p={3}
          bg={useColorModeValue("blue.50", "blue.900")}
          borderRadius="md"
          border="1px"
          borderColor={useColorModeValue("blue.200", "blue.700")}
        >
          <Text fontSize="xs" color="gray.600" fontWeight="bold" mb={1}>
            Status
          </Text>
          <Text fontSize="sm" fontWeight="bold" textTransform="capitalize">
            {project.status?.replace(/_/g, " ")}
          </Text>
        </Box>

        <Box
          p={3}
          bg={useColorModeValue("green.50", "green.900")}
          borderRadius="md"
          border="1px"
          borderColor={useColorModeValue("green.200", "green.700")}
        >
          <Text fontSize="xs" color="gray.600" fontWeight="bold" mb={1}>
            Open Tickets
          </Text>
          <Text fontSize="sm" fontWeight="bold">
            {
              projectTickets.filter(
                (t) =>
                  !["resolved", "closed", "completed"].includes(
                    t.workflowState,
                  ),
              ).length
            }
          </Text>
        </Box>

        <Box
          p={3}
          bg={useColorModeValue("orange.50", "orange.900")}
          borderRadius="md"
          border="1px"
          borderColor={useColorModeValue("orange.200", "orange.700")}
        >
          <Text fontSize="xs" color="gray.600" fontWeight="bold" mb={1}>
            Overdue
          </Text>
          <Text fontSize="sm" fontWeight="bold">
            {
              projectTickets.filter((t) => {
                if (!t.dueDate) return false;
                return (
                  new Date(t.dueDate) < new Date() &&
                  !["resolved", "closed", "completed"].includes(t.workflowState)
                );
              }).length
            }
          </Text>
        </Box>

        <Box
          p={3}
          bg={useColorModeValue("purple.50", "purple.900")}
          borderRadius="md"
          border="1px"
          borderColor={useColorModeValue("purple.200", "purple.700")}
        >
          <Text fontSize="xs" color="gray.600" fontWeight="bold" mb={1}>
            Team Members
          </Text>
          <Text fontSize="sm" fontWeight="bold">
            {project.members?.length ?? 0}
          </Text>
        </Box>

        <Box
          p={3}
          bg={useColorModeValue("teal.50", "teal.900")}
          borderRadius="md"
          border="1px"
          borderColor={useColorModeValue("teal.200", "teal.700")}
        >
          <Text fontSize="xs" color="gray.600" fontWeight="bold" mb={1}>
            Sprint Progress
          </Text>
          <Text fontSize="sm" fontWeight="bold">
            {dashboard?.activeSprint?.completionPercentage ?? 0}%
          </Text>
        </Box>
      </SimpleGrid>

      {/* Content */}
      <Box
        bg={bg}
        border="1px"
        borderColor={borderColor}
        borderRadius="lg"
        overflow="hidden"
      >
        <Tabs>
          <TabList
            px={4}
            bg={headerBg}
            borderBottom="1px"
            borderColor={borderColor}
          >
            <Tab>Overview</Tab>
            <Tab>Dashboard</Tab>
            <Tab>Tickets</Tab>
            <Tab>Status &amp; Transition</Tab>
          </TabList>

          <TabPanels>
            {/* ── Overview Tab ── */}
            <TabPanel p={6}>
              {project.description && (
                <Box
                  mb={6}
                  p={4}
                  bg={headerBg}
                  borderRadius="md"
                  borderLeft="4px solid"
                  borderLeftColor="blue.400"
                >
                  <Text fontSize="sm" color="gray.500" mb={1}>
                    Description
                  </Text>
                  <Text>{project.description}</Text>
                </Box>
              )}

              {/* Approval Workflow Stepper */}
              {project.status && (
                <Box
                  mb={8}
                  p={4}
                  bg={useColorModeValue("gray.50", "gray.700")}
                  borderRadius="md"
                >
                  <Text
                    fontWeight="semibold"
                    mb={4}
                    fontSize="sm"
                    textTransform="uppercase"
                    letterSpacing="wide"
                    color="blue.600"
                  >
                    Project Workflow
                  </Text>
                  <Stepper
                    index={
                      [
                        "draft",
                        "planning",
                        "active",
                        "on_hold",
                        "uat",
                        "deployment",
                        "completed",
                      ].indexOf(project.status) || 0
                    }
                  >
                    <Step>
                      <StepIndicator>
                        <StepStatus complete={<StepIcon />} />
                      </StepIndicator>
                      <Box flex="1">
                        <Text fontSize="sm" fontWeight="semibold">
                          Draft
                        </Text>
                      </Box>
                      <StepSeparator />
                    </Step>
                    <Step>
                      <StepIndicator>
                        <StepStatus complete={<StepIcon />} />
                      </StepIndicator>
                      <Box flex="1">
                        <Text fontSize="sm" fontWeight="semibold">
                          Planning
                        </Text>
                      </Box>
                      <StepSeparator />
                    </Step>
                    <Step>
                      <StepIndicator>
                        <StepStatus complete={<StepIcon />} />
                      </StepIndicator>
                      <Box flex="1">
                        <Text fontSize="sm" fontWeight="semibold">
                          Active
                        </Text>
                      </Box>
                      <StepSeparator />
                    </Step>
                    <Step>
                      <StepIndicator>
                        <StepStatus complete={<StepIcon />} />
                      </StepIndicator>
                      <Box flex="1">
                        <Text fontSize="sm" fontWeight="semibold">
                          Completed
                        </Text>
                      </Box>
                    </Step>
                  </Stepper>
                </Box>
              )}

              <Text
                fontWeight="semibold"
                mb={3}
                color="blue.600"
                fontSize="sm"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                Project Details
              </Text>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
                <InfoCard label="Type" value={project.projectType} />
                <InfoCard label="Billing" value={project.billingType} />
                <InfoCard
                  label="Priority"
                  value={project.priority}
                  color={`${PRIORITY_COLORS[project.priority] || "gray"}.500`}
                />
                <InfoCard
                  label="Health"
                  value={health.status}
                  color={`${health.color}.500`}
                />
                <InfoCard
                  label="Start Date"
                  value={
                    project.startDate
                      ? new Date(project.startDate).toLocaleDateString()
                      : "—"
                  }
                />
                <InfoCard
                  label="Target End Date"
                  value={
                    project.targetEndDate
                      ? new Date(project.targetEndDate).toLocaleDateString()
                      : "—"
                  }
                />
                <InfoCard
                  label="Team Members"
                  value={project.members?.length ?? 0}
                />
                <InfoCard
                  label="Created"
                  value={
                    project.createdAt
                      ? new Date(project.createdAt).toLocaleDateString()
                      : "—"
                  }
                />
              </SimpleGrid>

              {(project.portfolioId || project.programId) && (
                <>
                  <Divider mb={4} />
                  <Text
                    fontWeight="semibold"
                    mb={3}
                    color="blue.600"
                    fontSize="sm"
                    textTransform="uppercase"
                    letterSpacing="wide"
                  >
                    Hierarchy
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    {project.portfolioId && (
                      <InfoCard
                        label="Portfolio"
                        value={
                          project.portfolioId?.portfolioName ||
                          project.portfolioId
                        }
                      />
                    )}
                    {project.programId && (
                      <InfoCard
                        label="Program"
                        value={
                          project.programId?.programName || project.programId
                        }
                      />
                    )}
                  </SimpleGrid>
                </>
              )}
            </TabPanel>

            {/* ── Dashboard Tab ── */}
            <TabPanel p={6}>
              {dashLoading ? (
                <Flex justify="center" py={10}>
                  <Spinner color="blue.500" />
                </Flex>
              ) : dashboard ? (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {dashboard.ticketStats && (
                    <Box
                      p={5}
                      border="1px"
                      borderColor={borderColor}
                      borderRadius="lg"
                      bg={useColorModeValue("white", "gray.700")}
                      shadow="sm"
                      _hover={{ shadow: "md" }}
                      transition="all 0.2s"
                    >
                      <HStack mb={4} spacing={2}>
                        <FiBarChart2 size={20} />
                        <Text fontWeight="bold">Ticket Stats</Text>
                      </HStack>
                      {Object.entries(dashboard.ticketStats).map(([k, v]) => (
                        <Flex
                          key={k}
                          justify="space-between"
                          py={2}
                          borderBottom="1px"
                          borderColor={borderColor}
                          _last={{ border: "none" }}
                        >
                          <Text
                            fontSize="sm"
                            textTransform="capitalize"
                            color="gray.600"
                          >
                            {k}
                          </Text>
                          <Text fontSize="sm" fontWeight="bold">
                            {v}
                          </Text>
                        </Flex>
                      ))}
                    </Box>
                  )}

                  {dashboard.activeSprint && (
                    <Box
                      p={5}
                      border="1px"
                      borderColor={borderColor}
                      borderRadius="lg"
                      bg={useColorModeValue("white", "gray.700")}
                      shadow="sm"
                      _hover={{ shadow: "md" }}
                      transition="all 0.2s"
                    >
                      <HStack mb={4} spacing={2}>
                        <FiTrendingUp size={20} />
                        <Text fontWeight="bold">Active Sprint</Text>
                      </HStack>
                      <Text fontSize="sm" mb={3} fontWeight="semibold">
                        {dashboard.activeSprint.sprintName}
                      </Text>
                      <Progress
                        value={dashboard.activeSprint.completionPercentage || 0}
                        colorScheme="green"
                        borderRadius="full"
                        size="md"
                      />
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        {dashboard.activeSprint.completionPercentage || 0}%
                        complete
                      </Text>
                    </Box>
                  )}

                  {dashboard.sprintVelocity && (
                    <Box
                      p={5}
                      border="1px"
                      borderColor={borderColor}
                      borderRadius="lg"
                      bg={useColorModeValue("white", "gray.700")}
                      shadow="sm"
                      _hover={{ shadow: "md" }}
                      transition="all 0.2s"
                    >
                      <HStack mb={4} spacing={2}>
                        <FiBarChart2 size={20} />
                        <Text fontWeight="bold">Sprint Velocity</Text>
                      </HStack>
                      <Stat>
                        <StatLabel>Average Velocity</StatLabel>
                        <StatNumber fontSize="2xl">
                          {dashboard.sprintVelocity.average ?? "—"}
                        </StatNumber>
                      </Stat>
                    </Box>
                  )}

                  {dashboard.milestoneStats && (
                    <Box
                      p={5}
                      border="1px"
                      borderColor={borderColor}
                      borderRadius="lg"
                      bg={useColorModeValue("white", "gray.700")}
                      shadow="sm"
                      _hover={{ shadow: "md" }}
                      transition="all 0.2s"
                    >
                      <HStack mb={4} spacing={2}>
                        <BsLink45Deg size={20} />
                        <Text fontWeight="bold">Milestones</Text>
                      </HStack>
                      {Object.entries(dashboard.milestoneStats).map(
                        ([k, v]) => (
                          <Flex key={k} justify="space-between" py={2}>
                            <Text
                              fontSize="sm"
                              textTransform="capitalize"
                              color="gray.600"
                            >
                              {k}
                            </Text>
                            <Text fontSize="sm" fontWeight="bold">
                              {v}
                            </Text>
                          </Flex>
                        ),
                      )}
                    </Box>
                  )}

                  {!dashboard.ticketStats &&
                    !dashboard.activeSprint &&
                    !dashboard.milestoneStats && (
                      <EmptyStateCard
                        icon={FiBarChart2}
                        title="No Dashboard Data"
                        description="Dashboard data will appear once tickets, sprints, and milestones are created."
                      />
                    )}
                </SimpleGrid>
              ) : (
                <EmptyStateCard
                  icon={FiBarChart2}
                  title="Dashboard Loading"
                  description="Dashboard metrics will appear once your project has activity."
                />
              )}
            </TabPanel>

            {/* ── Tickets Tab ── ENHANCED ── */}
            <TabPanel p={6}>
              {ticketsLoading ? (
                <Flex justify="center" py={8}>
                  <Spinner color="blue.500" />
                </Flex>
              ) : projectTickets.length === 0 ? (
                <EmptyStateCard
                  icon={BsActivity}
                  title="No Tickets Linked"
                  description="Create or link tickets to track project work and manage team tasks."
                  action={
                    <Button
                      colorScheme="blue"
                      size="sm"
                      onClick={() => navigate(`/Ticketing/new?projectId=${id}`)}
                    >
                      Create First Ticket
                    </Button>
                  }
                />
              ) : (
                <VStack spacing={4} align="stretch">
                  {/* ── Filter Panel ── */}
                  <Box
                    p={4}
                    bg={useColorModeValue("gray.50", "gray.700")}
                    borderRadius="lg"
                    border="1px"
                    borderColor={borderColor}
                  >
                    <Flex
                      justify="space-between"
                      align="center"
                      mb={4}
                      flexWrap="wrap"
                      gap={2}
                    >
                      <Heading
                        size="sm"
                        textTransform="uppercase"
                        letterSpacing="wide"
                      >
                        Filters
                      </Heading>
                      <HStack spacing={2}>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setTicketFilters({
                              status: [],
                              priority: [],
                              severity: [],
                              assignedTo: [],
                              search: "",
                            });
                          }}
                        >
                          Reset
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="teal"
                          onClick={handleExportTickets}
                          leftIcon={<BsShieldLock />}
                        >
                          Export Excel
                        </Button>
                      </HStack>
                    </Flex>

                    {/* ── Filter Inputs ── */}
                    <SimpleGrid
                      columns={{ base: 1, md: 2, lg: 5 }}
                      spacing={3}
                      mb={4}
                    >
                      <Box>
                        <Text fontSize="xs" fontWeight="semibold" mb={1}>
                          Search
                        </Text>
                        <Input
                          placeholder="Ticket ID, title, description..."
                          size="sm"
                          value={ticketFilters.search}
                          onChange={(e) =>
                            setTicketFilters({
                              ...ticketFilters,
                              search: e.target.value,
                            })
                          }
                        />
                      </Box>

                      <Box>
                        <Text fontSize="xs" fontWeight="semibold" mb={1}>
                          Status
                        </Text>
                        <Menu closeOnSelect={false} matchWidth>
                          <MenuButton
                            as={Button}
                            size="sm"
                            variant="outline"
                            w="100%"
                            textAlign="left"
                            justifyContent="space-between"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                          >
                            {getMultiSelectLabel(
                              ticketFilters.status,
                              filterOptions.statuses || [],
                              "All statuses",
                            )}
                          </MenuButton>
                          <Portal>
                            <MenuList
                              maxH="260px"
                              overflowY="auto"
                              zIndex={2000}
                            >
                              <MenuOptionGroup
                                type="checkbox"
                                value={ticketFilters.status}
                                onChange={(values) =>
                                  setTicketFilters((prev) => ({
                                    ...prev,
                                    status: normalizeMultiValues(values),
                                  }))
                                }
                              >
                                {(filterOptions.statuses || []).map(
                                  (status) => (
                                    <MenuItemOption
                                      key={status.value}
                                      value={status.value}
                                    >
                                      {status.label}
                                    </MenuItemOption>
                                  ),
                                )}
                              </MenuOptionGroup>
                            </MenuList>
                          </Portal>
                        </Menu>
                      </Box>

                      <Box>
                        <Text fontSize="xs" fontWeight="semibold" mb={1}>
                          Priority
                        </Text>
                        <Menu closeOnSelect={false} matchWidth>
                          <MenuButton
                            as={Button}
                            size="sm"
                            variant="outline"
                            w="100%"
                            textAlign="left"
                            justifyContent="space-between"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                          >
                            {getMultiSelectLabel(
                              ticketFilters.priority,
                              filterOptions.priorities || [],
                              "All priorities",
                            )}
                          </MenuButton>
                          <Portal>
                            <MenuList
                              maxH="260px"
                              overflowY="auto"
                              zIndex={2000}
                            >
                              <MenuOptionGroup
                                type="checkbox"
                                value={ticketFilters.priority}
                                onChange={(values) =>
                                  setTicketFilters((prev) => ({
                                    ...prev,
                                    priority: normalizeMultiValues(values),
                                  }))
                                }
                              >
                                {(filterOptions.priorities || []).map(
                                  (priority) => (
                                    <MenuItemOption
                                      key={priority.value}
                                      value={priority.value}
                                    >
                                      {priority.label}
                                    </MenuItemOption>
                                  ),
                                )}
                              </MenuOptionGroup>
                            </MenuList>
                          </Portal>
                        </Menu>
                      </Box>

                      <Box>
                        <Text fontSize="xs" fontWeight="semibold" mb={1}>
                          Severity
                        </Text>
                        <Menu closeOnSelect={false} matchWidth>
                          <MenuButton
                            as={Button}
                            size="sm"
                            variant="outline"
                            w="100%"
                            textAlign="left"
                            justifyContent="space-between"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                          >
                            {getMultiSelectLabel(
                              ticketFilters.severity,
                              filterOptions.severities || [],
                              "All severities",
                            )}
                          </MenuButton>
                          <Portal>
                            <MenuList
                              maxH="260px"
                              overflowY="auto"
                              zIndex={2000}
                            >
                              <MenuOptionGroup
                                type="checkbox"
                                value={ticketFilters.severity}
                                onChange={(values) =>
                                  setTicketFilters((prev) => ({
                                    ...prev,
                                    severity: normalizeMultiValues(values),
                                  }))
                                }
                              >
                                {(filterOptions.severities || []).map(
                                  (severity) => (
                                    <MenuItemOption
                                      key={severity.value}
                                      value={severity.value}
                                    >
                                      {severity.label}
                                    </MenuItemOption>
                                  ),
                                )}
                              </MenuOptionGroup>
                            </MenuList>
                          </Portal>
                        </Menu>
                      </Box>

                      <Box>
                        <Text fontSize="xs" fontWeight="semibold" mb={1}>
                          Assignee
                        </Text>
                        <Menu closeOnSelect={false} matchWidth>
                          <MenuButton
                            as={Button}
                            size="sm"
                            variant="outline"
                            w="100%"
                            textAlign="left"
                            justifyContent="space-between"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                          >
                            {getMultiSelectLabel(
                              ticketFilters.assignedTo,
                              assigneeOptions,
                              "All assignees",
                            )}
                          </MenuButton>
                          <Portal>
                            <MenuList
                              maxH="260px"
                              overflowY="auto"
                              zIndex={2000}
                            >
                              <MenuOptionGroup
                                type="checkbox"
                                value={ticketFilters.assignedTo}
                                onChange={(values) =>
                                  setTicketFilters((prev) => ({
                                    ...prev,
                                    assignedTo: normalizeMultiValues(values),
                                  }))
                                }
                              >
                                {assigneeOptions.map((assignee) => (
                                  <MenuItemOption
                                    key={assignee.value}
                                    value={assignee.value}
                                  >
                                    {assignee.label}
                                  </MenuItemOption>
                                ))}
                              </MenuOptionGroup>
                            </MenuList>
                          </Portal>
                        </Menu>
                      </Box>
                    </SimpleGrid>

                    {/* ── Filter Summary ── */}
                    {hasActiveFilters && (
                      <Text fontSize="xs" color="gray.600">
                        Showing {displayedTickets.length} of{" "}
                        {projectTickets.length} tickets
                      </Text>
                    )}
                  </Box>

                  {/* ── Tickets Table ── */}
                  <Box
                    border="1px"
                    borderColor={borderColor}
                    borderRadius="lg"
                    overflow="auto"
                  >
                    {hasActiveFilters && displayedTickets.length === 0 ? (
                      <VStack py={10} spacing={2}>
                        <Text fontWeight="semibold">
                          No tickets match these filters
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          Adjust filters or press Reset to view all tickets.
                        </Text>
                      </VStack>
                    ) : (
                      <Table size="sm" variant="simple">
                        <Thead
                          bg={headerBg}
                          position="sticky"
                          top={0}
                          zIndex={1}
                        >
                          <Tr>
                            <Th>Ticket</Th>
                            <Th>Title</Th>
                            <Th>Status</Th>
                            <Th>Priority</Th>
                            <Th>Severity</Th>
                            <Th>Assigned To</Th>
                            <Th>Created</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {displayedTickets.map((ticket) => {
                            const workflowState =
                              ticket.workflowState || "open";
                            const isOpen = ![
                              "resolved",
                              "closed",
                              "completed",
                            ].includes(workflowState);
                            return (
                              <Tr
                                key={ticket._id}
                                _hover={{
                                  bg: headerBg,
                                  cursor: "pointer",
                                  shadow: isOpen ? "md" : "none",
                                  transform: isOpen
                                    ? "translateY(-1px)"
                                    : "none",
                                }}
                                transition="all 0.15s ease"
                                borderLeft="4px solid"
                                borderLeftColor={
                                  WORKFLOW_STATE_COLORS[workflowState]
                                    ? `${WORKFLOW_STATE_COLORS[workflowState]}.300`
                                    : "gray.300"
                                }
                                onClick={() =>
                                  navigate(`/Ticketing/${ticket._id}`)
                                }
                              >
                                <Td>
                                  <Text
                                    fontSize="sm"
                                    fontWeight="semibold"
                                    color="blue.500"
                                  >
                                    {ticket.ticketId ||
                                      ticket.ticketCode ||
                                      "-"}
                                  </Text>
                                </Td>
                                <Td maxW="360px">
                                  <Text noOfLines={1}>
                                    {ticket.title || "-"}
                                  </Text>
                                </Td>
                                <Td>
                                  <Badge
                                    colorScheme={
                                      WORKFLOW_STATE_COLORS[workflowState] ||
                                      "gray"
                                    }
                                  >
                                    {String(workflowState).replace(/_/g, " ")}
                                  </Badge>
                                </Td>
                                <Td>
                                  <Badge
                                    colorScheme={
                                      PRIORITY_COLORS[ticket.priority] || "gray"
                                    }
                                  >
                                    {ticket.priority || "-"}
                                  </Badge>
                                </Td>
                                <Td>{ticket.severity || "-"}</Td>
                                <Td>
                                  {ticket.assignedTo?.name ||
                                    ticket.assignedTo?.email ||
                                    "Unassigned"}
                                </Td>
                                <Td>
                                  {ticket.createdAt
                                    ? new Date(
                                        ticket.createdAt,
                                      ).toLocaleDateString()
                                    : "-"}
                                </Td>
                              </Tr>
                            );
                          })}
                        </Tbody>
                      </Table>
                    )}
                  </Box>
                </VStack>
              )}
            </TabPanel>
            {/* ── Status & Transition Tab ── */}
            <TabPanel p={6}>
              <Box
                p={6}
                bg={useColorModeValue(
                  `${STATUS_COLORS[project.status] || "gray"}.50`,
                  `${STATUS_COLORS[project.status] || "gray"}.900`,
                )}
                borderRadius="lg"
                border="2px"
                borderColor={useColorModeValue(
                  `${STATUS_COLORS[project.status] || "gray"}.200`,
                  `${STATUS_COLORS[project.status] || "gray"}.700`,
                )}
                mb={8}
              >
                <VStack align="flex-start" spacing={3}>
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    textTransform="uppercase"
                    color="gray.600"
                    letterSpacing="wide"
                  >
                    Current Project Status
                  </Text>
                  <HStack spacing={3} align="center">
                    {SEMANTIC_STATUS[project.status]?.icon &&
                      React.createElement(
                        SEMANTIC_STATUS[project.status].icon,
                        {
                          size: 28,
                          color: `var(--chakra-colors-${STATUS_COLORS[project.status] || "gray"}-500)`,
                        },
                      )}
                    <Box>
                      <Text
                        fontSize="2xl"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        {project.status?.replace(/_/g, " ")}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {SEMANTIC_STATUS[project.status]?.description || ""}
                      </Text>
                    </Box>
                  </HStack>
                </VStack>
              </Box>

              <Text
                fontWeight="semibold"
                mb={4}
                color="blue.600"
                fontSize="sm"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                Next Actions
              </Text>

              {(STATUS_TRANSITIONS[project.status] || []).length > 0 ? (
                <Wrap spacing={3} mb={8}>
                  {(STATUS_TRANSITIONS[project.status] || []).map((s) => (
                    <WrapItem key={s}>
                      <Button
                        size="md"
                        colorScheme={STATUS_COLORS[s] || "gray"}
                        variant="solid"
                        isLoading={transitioning === s}
                        onClick={() => handleTransition(s)}
                        leftIcon={
                          SEMANTIC_STATUS[s]?.icon
                            ? React.createElement(SEMANTIC_STATUS[s].icon)
                            : undefined
                        }
                        _hover={{
                          shadow: "md",
                          transform: "translateY(-2px)",
                        }}
                        transition="all 0.2s"
                      >
                        {s.replace(/_/g, " ")}
                      </Button>
                    </WrapItem>
                  ))}
                </Wrap>
              ) : (
                <Alert status="info" mb={8}>
                  <AlertIcon />
                  This project has reached a terminal state. No further
                  transitions available.
                </Alert>
              )}

              {project.statusHistory?.length > 0 && (
                <Box>
                  <Divider mb={6} />
                  <Text
                    fontWeight="semibold"
                    mb={4}
                    color="blue.600"
                    fontSize="sm"
                    textTransform="uppercase"
                    letterSpacing="wide"
                  >
                    Status Timeline
                  </Text>
                  <VStack align="stretch" spacing={3}>
                    {project.statusHistory.map((h, i) => (
                      <Flex
                        key={i}
                        align="center"
                        gap={4}
                        p={3}
                        bg={useColorModeValue("gray.50", "gray.700")}
                        borderRadius="md"
                        borderLeft="4px solid"
                        borderLeftColor={`${STATUS_COLORS[h.to] || "gray"}.400`}
                      >
                        <Badge
                          colorScheme={STATUS_COLORS[h.from] || "gray"}
                          variant="outline"
                          px={3}
                          py={1}
                        >
                          {h.from?.replace(/_/g, " ")}
                        </Badge>
                        <FiBarChart2 color="gray" opacity={0.5} />
                        <Badge
                          colorScheme={STATUS_COLORS[h.to] || "gray"}
                          px={3}
                          py={1}
                        >
                          {h.to?.replace(/_/g, " ")}
                        </Badge>
                        <Text color="gray.500" ml="auto" fontSize="sm">
                          {h.changedAt
                            ? new Date(h.changedAt).toLocaleDateString()
                            : ""}
                        </Text>
                      </Flex>
                    ))}
                  </VStack>
                </Box>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  );
}
